import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef } from 'react';

/**
 * @typedef {Object} CardProps
 * @property {React.ReactNode} children - Card content
 * @property {string} [variant] - Card variant (default, form)
 * @property {number} [alternateIndex] - Index (kept for API compatibility)
 * @property {string} [color] - Accent color (kept for API compatibility)
 * @property {string} [backgroundColor] - Background color
 * @property {number} [cornerSize] - Kept for API compatibility
 * @property {number} [borderWidth] - Kept for API compatibility
 * @property {number} [borderRadius] - Radius for rounded corners
 * @property {boolean} [hover] - Enable hover effects
 * @property {Object} [sx] - Additional MUI sx props
 * @property {Function} [onClick] - Click handler
 */

/**
 * Reusable Card component with clean rounded corners
 * Updated to match the rounded EllaFin aesthetic (corner accents removed)
 *
 * Features:
 * - Clean rounded corners
 * - Customizable border radius
 * - Hover effects
 * - Single source of truth for card styling
 *
 * @param {CardProps} props - Component props
 * @param {React.Ref} ref - Forwarded ref
 * @returns {JSX.Element} Card component
 */
const Card = forwardRef(
  (
    {
      children,
      variant = 'default',
      alternateIndex: _alternateIndex = 0,
      color: colorProp,
      backgroundColor = 'white',
      cornerSize: _cornerSize = 30,
      borderWidth: _borderWidth = 4,
      borderRadius = 16,
      hover = true,
      sx = {},
      onClick,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const color = colorProp || theme.palette.secondary.main;

    // Variant-specific styles
    const variantStyles = {
      default: {
        p: 3,
        minHeight: '160px',
      },
      form: {
        p: 4,
      },
      ebook: {
        p: 4,
        height: '280px',
      },
      approach: {
        p: 4,
        height: '100%',
      },
    };

    return (
      <Box
        ref={ref}
        onClick={onClick}
        sx={{
          backgroundColor,
          borderRadius: `${borderRadius}px`,
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s ease',
          border: '1px solid #e8e8e8',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          cursor: onClick ? 'pointer' : 'default',
          position: 'relative',
          overflow: 'hidden',
          containerType: 'inline-size',
          // Base styles from variant
          ...(variantStyles[variant] || variantStyles.default),
          // Responsive fallback
          '@media (max-width: 500px)': {
            p: variant === 'form' ? 3 : 2,
            minHeight: variant === 'ebook' ? '260px' : '140px',
          },
          // Container queries for modern browsers
          '@container (max-width: 250px)': {
            p: variant === 'form' ? 3 : 2,
            minHeight: variant === 'ebook' ? '260px' : '140px',
          },
          '@container (min-width: 350px)': {
            p: variant === 'form' ? 4 : 3,
            minHeight: variant === 'ebook' ? '300px' : '180px',
          },
          // Hover effects
          ...(hover && {
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 30px ${color}40`,
              backgroundColor:
                variant === 'default' ? `${color}05` : backgroundColor,
            },
          }),
          // Additional sx props override
          ...sx,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }
);

Card.displayName = 'Card';

export default Card;
